import { useQuery } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import type {
  OoseResolutionDetail as ApiOoseResolutionDetail,
  OoseConflictsResponse,
  OoseResolutionStatus,
} from '@lio/api-types';

/** Includes the server-calculated/persisted net Policy Impact. */
export type OoseResolutionDetail = ApiOoseResolutionDetail & {
  policyImpact?: number | null;
};
export type { OoseResolutionStatus };

// Unified query key for both the banner check and the full resolution detail.
// Both use the same GET /policy/:policyId/oose-resolution endpoint.
export const ooseResolutionQueryKeys = {
  get: (policyId: string) => ['oose-resolution', { policyId }] as const,
};

/**
 * Fetches the active (InProgress or Processing) OoSE resolution for a policy.
 * Returns null when no active resolution exists (API returns 200 null).
 */
export const useGetOoseResolution = ({ policyId }: { policyId: string | undefined }) => {
  const client = useClient();

  const result = useQuery<OoseResolutionDetail | null>({
    queryKey: ooseResolutionQueryKeys.get(policyId ?? ''),
    queryFn: async () => {
      if (!policyId) return null;
      return await client.get(`policy/${policyId}/oose-resolution`).json<OoseResolutionDetail | null>();
    },
    enabled: !!policyId,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  });

  return { ...result, ooseResolution: result.data ?? null };
};

/**
 * Fetches the full OoSE resolution detail (with items, re-rating status, etc.)
 * for the active resolution on a policy. Polls while any item is re-rating.
 *
 * Uses the same query key as useGetOoseResolution — no duplicate requests.
 */
export const useGetOoseResolutionDetail = ({ policyId }: { policyId: string | undefined }) => {
  const client = useClient();

  const result = useQuery<OoseResolutionDetail | null>({
    queryKey: ooseResolutionQueryKeys.get(policyId ?? ''),
    queryFn: async () => {
      if (!policyId) return null;
      return await client.get(`policy/${policyId}/oose-resolution`).json<OoseResolutionDetail | null>();
    },
    enabled: !!policyId,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchIntervalInBackground: false,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const hasInProgressRating = data.items?.some((item) => item.reratingStatus === 'InProgress');
      const isCommitProcessing = String(data.status) === 'Processing';
      return hasInProgressRating || isCommitProcessing ? 3_000 : false;
    },
    staleTime: 5_000,
  });

  return { ...result, resolution: result.data ?? null };
};

/**
 * Fetches conflicts lazily for a single resolution item.
 * Returns { conflicts, autoMerged } via GET /oose-resolution/:id/item/:itemId/conflicts
 */
export const useGetOoseItemConflicts = (resolutionId: string, itemId: string, enabled: boolean) => {
  const client = useClient();

  return useQuery<OoseConflictsResponse>({
    queryKey: ['oose-item-conflicts', resolutionId, itemId],
    queryFn: () => client.get(`oose-resolution/${resolutionId}/item/${itemId}/conflicts`).json<OoseConflictsResponse>(),
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
};
